/**
 * The model as a picture: node types as cards, relationships as lines.
 *
 * Positions are the ones stored on each node type, so a layout the user drags
 * into shape is theirs and survives a reload. A type with no stored position
 * gets a place on a ring rather than a pile at the origin.
 *
 * Selecting a type dims everything it does not touch — on a model with twenty
 * relationships, that is the difference between a diagram and a hairball.
 *
 * The view itself pans and zooms. That is a viewBox, not a transform, so drag
 * coordinates keep mapping back to model space at any magnification and a moved
 * node is stored where it looks like it was dropped. The viewBox takes the
 * panel's own aspect ratio rather than a fixed one: a fixed ratio letterboxes,
 * and on a tall workspace pane that wastes most of the canvas.
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { edgeColour, nodeColour } from './palette'
import { GraphNodeCard } from './GraphNodeCard'
import type { EdgeTypeDef, NodeTypeDef } from '../types/brain.types'

/** The coordinate space stored positions are expressed in. */
const VIEW_W = 900
const VIEW_H = 520
const MIN_SPAN = VIEW_W / 3
const MAX_SPAN = VIEW_W * 2.5

interface Placed {
  node: NodeTypeDef
  x: number
  y: number
  r: number
}

/** The visible window, in model coordinates. */
interface View {
  x: number
  y: number
  w: number
  h: number
}

const HOME: View = { x: 0, y: 0, w: VIEW_W, h: VIEW_H }


function place(nodes: NodeTypeDef[]): Placed[] {
  const missing = nodes.filter((n) => !n.position)
  return nodes.map((node) => {
    const index = missing.indexOf(node)
    let x = node.position?.x
    let y = node.position?.y

    if (x === undefined || y === undefined) {
      const angle = -Math.PI / 2 + (index / Math.max(1, missing.length)) * Math.PI * 2
      x = VIEW_W / 2 + 190 * Math.cos(angle)
      y = VIEW_H / 2 + 150 * Math.sin(angle)
    }

    // Reserve enough room for the full card when fitting the view.
    const r = 84
    return { node, x, y, r }
  })
}

export function GraphCanvas({
  nodeTypes,
  edgeTypes,
  selectedId,
  onSelect,
  onMove,
  editable,
  overlay,
}: {
  nodeTypes: NodeTypeDef[]
  edgeTypes: EdgeTypeDef[]
  selectedId: string | null
  /** Null clears the selection — clicking empty space is a real choice. */
  onSelect: (nodeTypeId: string | null) => void
  onMove?: (nodeTypeId: string, x: number, y: number) => void
  editable?: boolean
  /** Rendered over the canvas — the legend, in practice. */
  overlay?: React.ReactNode
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [panFrom, setPanFrom] = useState<{ x: number; y: number; view: View } | null>(null)
  const [overrides, setOverrides] = useState<Record<string, { x: number; y: number }>>({})
  const [view, setView] = useState<View>(HOME)
  const [hovered, setHovered] = useState<string | null>(null)
  /** Whether the press in flight has travelled far enough to be a pan. */
  const panMoved = useRef(false)

  // Selection wins over hover, so pointing at the diagram previews a
  // neighbourhood without throwing away the one you are working on.
  const focusId = selectedId ?? hovered

  const placed = useMemo(() => {
    const base = place(nodeTypes)
    return base.map((p) =>
      overrides[p.node.node_type_id] ? { ...p, ...overrides[p.node.node_type_id] } : p,
    )
  }, [nodeTypes, overrides])

  const byId = useMemo(() => {
    const map: Record<string, Placed> = {}
    for (const p of placed) map[p.node.node_type_id] = p
    return map
  }, [placed])

  const neighbours = useMemo(() => {
    if (!focusId) return null
    const set = new Set<string>([focusId])
    for (const edge of edgeTypes) {
      if (edge.from_node_type_id === focusId) set.add(edge.to_node_type_id)
      if (edge.to_node_type_id === focusId) set.add(edge.from_node_type_id)
    }
    return set
  }, [focusId, edgeTypes])

  /** The panel's shape, so the viewBox can match it instead of letterboxing. */
  const aspect = useCallback(() => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect || !rect.width || !rect.height) return VIEW_W / VIEW_H
    return rect.width / rect.height
  }, [])

  /** Frames everything that is drawn, with room to breathe around the edges. */
  const fit = useCallback(() => {
    const ratio = aspect()
    if (!placed.length) return setView({ ...HOME, w: VIEW_H * ratio })

    const pad = 50
    const minX = Math.min(...placed.map((p) => p.x - p.r)) - pad
    const maxX = Math.max(...placed.map((p) => p.x + p.r)) + pad
    const minY = Math.min(...placed.map((p) => p.y - p.r)) - pad
    const maxY = Math.max(...placed.map((p) => p.y + p.r)) + pad

    // Whichever axis is tighter decides the scale; the other gets the slack, so
    // nothing is ever cropped.
    let w = Math.max(maxX - minX, (maxY - minY) * ratio)
    // Fitting zooms out, never in. A model with three types near the middle has
    // a tiny bounding box, and scaling that up to fill the panel blows the
    // circles up to the size of the pane — it reads as if the graph opened
    // maximised. Below the home span we just show the home span.
    w = Math.min(MAX_SPAN, Math.max(VIEW_H * ratio, w))
    const h = w / ratio
    setView({ x: (minX + maxX) / 2 - w / 2, y: (minY + maxY) / 2 - h / 2, w, h })
  }, [placed, aspect])

  /** Zooms about the middle of the view, so what you were looking at stays put. */
  function zoomBy(factor: number) {
    setView((v) => {
      const w = Math.min(MAX_SPAN, Math.max(MIN_SPAN, v.w / factor))
      const h = w / (v.w / v.h)
      return { x: v.x + v.w / 2 - w / 2, y: v.y + v.h / 2 - h / 2, w, h }
    })
  }

  // A new graph opens framed — once. After that the view is the user's, and
  // nothing rescales it on its own: not a model change, and not a resize of the
  // panel. An automatic refit reads as the diagram zooming by itself, which
  // makes the canvas feel like it is fighting you. The Fit button is the way
  // back, and it is a deliberate click.
  const fitted = useRef(false)
  const fitRef = useRef(fit)
  fitRef.current = fit
  useLayoutEffect(() => {
    if (!svgRef.current || !placed.length || fitted.current) return
    fitted.current = true
    fitRef.current()
  }, [placed.length])

  const toCanvas = useCallback(
    (event: { clientX: number; clientY: number }): { x: number; y: number } | null => {
      const svg = svgRef.current
      if (!svg) return null
      const rect = svg.getBoundingClientRect()
      return {
        x: view.x + ((event.clientX - rect.left) / rect.width) * view.w,
        y: view.y + ((event.clientY - rect.top) / rect.height) * view.h,
      }
    },
    [view],
  )

  if (!nodeTypes.length) {
    return (
      <div className="brain-canvas" style={{ display: 'grid', placeItems: 'center' }}>
        <p className="muted small" style={{ maxWidth: '46ch', textAlign: 'center' }}>
          No node types yet. Add one from the Nodes tab, or use Generate to draft a model from a
          description of what you need.
        </p>
      </div>
    )
  }

  return (
    <>
      {overlay}
      <svg
        ref={svgRef}
        className="brain-canvas"
        viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Node types and the relationships between them"
        style={{ cursor: panFrom ? 'grabbing' : undefined }}
        onPointerDown={(event) => {
          // Anything that starts on the background pans the view — and, if it
          // turns out not to have moved, counts as a click on empty space.
          if (event.target === event.currentTarget) {
            panMoved.current = false
            setPanFrom({ x: event.clientX, y: event.clientY, view })
          }
        }}
        onPointerMove={(event) => {
          if (dragging) {
            const point = toCanvas(event)
            if (point) setOverrides((prev) => ({ ...prev, [dragging]: point }))
            return
          }
          if (panFrom) {
            const svg = svgRef.current
            if (!svg) return
            const rect = svg.getBoundingClientRect()
            const dx = ((event.clientX - panFrom.x) / rect.width) * panFrom.view.w
            const dy = ((event.clientY - panFrom.y) / rect.height) * panFrom.view.h
            // A few pixels of travel is a shaky click, not a drag; past that
            // the gesture is a pan and must not also clear the selection.
            if (Math.abs(event.clientX - panFrom.x) > 3 || Math.abs(event.clientY - panFrom.y) > 3) {
              panMoved.current = true
            }
            setView({ ...panFrom.view, x: panFrom.view.x - dx, y: panFrom.view.y - dy })
          }
        }}
        onPointerUp={() => {
          if (dragging && onMove) {
            const moved = overrides[dragging]
            if (moved) onMove(dragging, Math.round(moved.x), Math.round(moved.y))
          }
          // A press on empty space that never became a pan is a click on
          // nothing, which puts the canvas back to its resting state.
          if (panFrom && !panMoved.current) onSelect(null)
          setDragging(null)
          setPanFrom(null)
        }}
        onPointerLeave={() => {
          setDragging(null)
          setPanFrom(null)
          setHovered(null)
        }}
      >
        <defs>
          {/* Laid out in model space, so the grid pans and zooms with the
              diagram and gives the drag something to move against. */}
          <pattern id="brain-gdots" width="52" height="52" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.9" className="gdot" />
          </pattern>
          <radialGradient id="brain-gvignette">
            <stop offset="0%" className="gvig-in" />
            <stop offset="100%" className="gvig-out" />
          </radialGradient>

        </defs>
        <rect
          x={view.x}
          y={view.y}
          width={view.w}
          height={view.h}
          fill="url(#brain-gvignette)"
          pointerEvents="none"
        />
        <rect
          x={view.x}
          y={view.y}
          width={view.w}
          height={view.h}
          fill="url(#brain-gdots)"
          pointerEvents="none"
        />

        <g>
          {edgeTypes.map((edge) => {
            const from = byId[edge.from_node_type_id]
            const to = byId[edge.to_node_type_id]
            if (!from || !to) return null
            const touches =
              edge.from_node_type_id === focusId || edge.to_node_type_id === focusId
            const dim = Boolean(neighbours) && !touches
            // Every relationship of the same name is drawn the same colour, so
            // one kind of edge can be followed across a busy diagram.
            const colour = edgeColour(edge.key)
            return (
              <g key={edge.edge_type_id}>
                <line
                  className={`glink${dim ? ' dim' : ''}${touches ? ' lit' : ''}`}
                  stroke={colour}
                  strokeWidth={touches ? 2.4 : 1.5}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                />
                {/* The relationship's name always shows — a line with no name
                    on it is the one thing a model diagram cannot afford. */}
                <text
                  className={`edgelabel${dim ? ' dim' : ''}${touches ? ' lit' : ''}`}
                  x={(from.x + to.x) / 2}
                  y={(from.y + to.y) / 2 - 5}
                >
                  {edge.key}
                </text>
              </g>
            )
          })}
        </g>

        <g>
          {placed.map(({ node, x, y }) => {
            const dim = neighbours ? !neighbours.has(node.node_type_id) : false
            const selected = node.node_type_id === selectedId
            const colour = nodeColour(node.node_type_id)
            return (
              <g
                key={node.node_type_id}
                className={`gnode${dim ? ' dim' : ''}`}
                data-sel={selected}
                tabIndex={0}
                role="button"
                aria-label={`${node.key}, ${node.category}, ${node.record_count ?? 0} records`}
                onClick={() => onSelect(node.node_type_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onSelect(node.node_type_id)
                }}
                // Not while a drag or a pan is in flight: the dimming would
                // flicker under the cursor as the diagram moves past it.
                onPointerEnter={() => {
                  if (!dragging && !panFrom) setHovered(node.node_type_id)
                }}
                onPointerLeave={() => setHovered((id) => (id === node.node_type_id ? null : id))}
                onPointerDown={(e) => {
                  if (!editable) return
                  e.preventDefault()
                  e.stopPropagation()
                  onSelect(node.node_type_id)
                  setDragging(node.node_type_id)
                }}
                style={editable ? { cursor: 'grab' } : undefined}
              >
                <GraphNodeCard x={x} y={y} title={node.display_name || node.key}
                  subtitle={`${node.category} · ${(node.record_count ?? 0).toLocaleString()} records`}
                  colour={colour} />
              </g>
            )
          })}
        </g>
      </svg>

      <div className="cp-zoom">
        <button onClick={() => zoomBy(1.25)} title="Zoom in" aria-label="Zoom in">
          +
        </button>
        <button onClick={() => zoomBy(1 / 1.25)} title="Zoom out" aria-label="Zoom out">
          −
        </button>
        <button
          onClick={fit}
          title="Fit to the model"
          aria-label="Fit to the model"
        >
          ⤢
        </button>
      </div>
    </>
  )
}
